import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Package, Upload, Download, LogOut, Eye, Images, ChevronLeft, ChevronRight } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import ProductFormDialog from '@/components/admin/ProductFormDialog';
import DangerZone from '@/components/admin/DangerZone';
import ProductHistoryModal from '@/components/admin/ProductHistoryModal';
import { useAdminRole } from '@/hooks/useAdminRole';
import { History } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
};

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

const AdminPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('products');
  const [editingProduct, setEditingProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [brands, setBrands] = useState({});
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [existingGroups, setExistingGroups] = useState([]); // [{name, variant_count}]
  const [existingAttributes, setExistingAttributes] = useState([]); // [{key, usage_count, values: []}]
  const [imageFixRunning, setImageFixRunning] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [historyForProduct, setHistoryForProduct] = useState(null);
  const role = useAdminRole();
  const isSuperAdmin = role === 'super_admin';
  const PAGE_SIZE = 20;

  const handleLogout = useCallback(() => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  }, [navigate]);

  const [categories, setCategories] = useState([]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      const list = (response.data.categories || []).map((c) => ({
        value: c.id,
        label: c.name,
      }));
      setCategories(list);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchBrands = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data.brands);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`, { params: { limit: 1000, include_inactive: true } });
      setProducts(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching products:', error);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      setExistingGroups(response.data.groups || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching groups:', error);
    }
  }, []);

  const fetchAttributes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/variant-attributes`);
      setExistingAttributes(response.data.attributes || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching attributes:', error);
    }
  }, []);

  // Apply admin search + category filter client-side (full list already fetched)
  const filteredProducts = products.filter((p) => {
    if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
    if (statusFilter === 'active' && p.active === false) return false;
    if (statusFilter === 'inactive' && p.active !== false) return false;
    const q = productSearch.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      p.product_code || '', p.name || '', p.brand || '', p.barcode || '', p.group_name || ''
    ].join(' ').toLowerCase();
    return q.split(/\s+/).every((t) => haystack.includes(t));
  });

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedProducts = filteredProducts.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    if (start > 2) pages.push('…');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push('…');
    pages.push(totalPages);
    return pages;
  };

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`, getAuthHeaders());
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  }, [handleLogout]);

  useEffect(() => {
    fetchProducts();
    fetchOrders();
    fetchBrands();
    fetchCategories();
    fetchGroups();
    fetchAttributes();
  }, [fetchProducts, fetchOrders, fetchBrands, fetchCategories, fetchGroups, fetchAttributes]);

  const handleProductSaved = () => {
    fetchProducts();
    fetchGroups();
    fetchAttributes();
  };

  const handleAutoFixImages = async () => {
    if (!window.confirm(
      'This will scan all products and replace dark/uniform-color backdrops with white for a clean storefront look. Real-life photos with textured backgrounds will be skipped. Continue?'
    )) return;
    setImageFixRunning(true);
    try {
      const res = await axios.post(`${API}/admin/reprocess-images`, null, getAuthHeaders());
      const r = res.data;
      const examples = (r.examples || []).slice(0, 4).join(', ');
      toast.success(
        `Scanned ${r.scanned} products: fixed ${r.fixed}${r.skipped_unreachable ? `, skipped ${r.skipped_unreachable} unreachable` : ''}${r.errors ? `, ${r.errors} errors` : ''}${examples ? ` — e.g. ${examples}` : ''}`,
        { duration: 8000 }
      );
      fetchProducts();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Auto-fix failed:', error);
      toast.error('Auto-fix failed: ' + (error?.response?.data?.detail || error.message));
    } finally {
      setImageFixRunning(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setIsDialogOpen(true);
  };

  const handleToggleTopSelling = async (product) => {
    const next = !product.is_top_selling;
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, is_top_selling: next } : p)));
    try {
      await axios.put(`${API}/products/${product.id}`, { is_top_selling: next }, getAuthHeaders());
      toast.success(next ? `"${product.name}" added to Top Selling` : `"${product.name}" removed from Top Selling`);
    } catch (error) {
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, is_top_selling: !next } : p)));
      toast.error('Failed to update Top Selling flag');
    }
  };

  const handleToggleActive = async (product) => {
    const next = product.active === false;
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, active: next } : p)));
    try {
      await axios.put(`${API}/products/${product.id}`, { active: next }, getAuthHeaders());
      toast.success(next ? `"${product.name}" is now visible on the website` : `"${product.name}" hidden from the website`);
    } catch (error) {
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, active: !next } : p)));
      toast.error('Failed to update product status');
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/products/bulk-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      const parts = [];
      if (response.data.products_added > 0) parts.push(`${response.data.products_added} created`);
      if (response.data.products_updated > 0) parts.push(`${response.data.products_updated} updated`);
      if (response.data.products_unchanged > 0) parts.push(`${response.data.products_unchanged} unchanged`);
      toast.success(`Import complete: ${parts.join(', ') || 'no changes'}`, { duration: 6000 });
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} row(s) had issues: ${response.data.errors[0]}`, { duration: 8000 });
        console.error('Import errors:', response.data.errors);
      }

      setIsBulkUploadOpen(false);
      setUploadFile(null);
      fetchProducts();
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload products');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/products/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('All products exported');
    } catch (error) {
      toast.error('Failed to export products');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-2xl font-outfit font-bold tracking-tight text-slate-900">
            Admin Dashboard
          </h1>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="border-red-500 text-red-500 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <div className="flex items-center justify-between mb-5">{activeTab === 'products' && (
            <div className="flex gap-3">
              <Dialog open={isBulkUploadOpen} onOpenChange={setIsBulkUploadOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-[#0047AB] text-[#0047AB] hover:bg-[#0047AB]/10"
                    data-testid="bulk-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Bulk Upload
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Import / Export Products</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Button
                      variant="outline"
                      onClick={downloadTemplate}
                      className="w-full"
                      data-testid="download-template-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export All Products (CSV Template)
                    </Button>
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 space-y-1">
                      <p>• The export contains every product with its <strong>Product ID</strong> and all fields (except images).</p>
                      <p>• Edit any fields and re-upload: rows <strong>with a Product ID</strong> update that product.</p>
                      <p>• Rows with a <strong>blank Product ID</strong> create new products.</p>
                      <p>• Columns marked with <strong>*</strong> are mandatory for new products. Blank cells never overwrite existing values.</p>
                      <p>• Variant attributes format: <span className="font-mono">Color:Blue; Storage:256GB</span></p>
                    </div>
                    <div className="space-y-2">
                      <Label>Upload CSV or Excel File</Label>
                      <Input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        data-testid="file-upload-input"
                      />
                    </div>

                    <Button
                      onClick={handleBulkUpload}
                      disabled={!uploadFile || uploading}
                      className="w-full bg-[#0047AB] hover:bg-[#003380]"
                      data-testid="upload-submit-btn"
                    >
                      {uploading ? 'Importing...' : 'Import Products'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button
                variant="outline"
                className="border-[#0047AB] text-[#0047AB] hover:bg-[#0047AB]/10"
                disabled={imageFixRunning}
                onClick={handleAutoFixImages}
                data-testid="autofix-images-btn"
                title="Detects products with dark/uniform backdrops and replaces them with white for a clean storefront look"
              >
                <Images className="w-4 h-4 mr-2" />
                {imageFixRunning ? 'Fixing…' : 'Auto-fix Images'}
              </Button>

              <Button
                className="bg-[#0047AB] hover:bg-[#003380]"
                onClick={() => { setEditingProduct(null); setIsDialogOpen(true); }}
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>

              <ProductFormDialog
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                editingProduct={editingProduct}
                brands={brands}
                categories={categories}
                existingGroups={existingGroups}
                existingAttributes={existingAttributes}
                onSaved={handleProductSaved}
              />
          </div>
          )}
        </div>

        <div className="flex gap-3 mb-5">
          <Button
            variant={activeTab === 'products' ? 'default' : 'outline'}
            className={activeTab === 'products' ? 'bg-[#0047AB] hover:bg-[#003380]' : ''}
            onClick={() => setActiveTab('products')}
            data-testid="products-tab"
          >
            <Package className="w-4 h-4 mr-2" />
            Products ({products.length})
          </Button>
          <Button
            variant={activeTab === 'orders' ? 'default' : 'outline'}
            className={activeTab === 'orders' ? 'bg-[#0047AB] hover:bg-[#003380]' : ''}
            onClick={() => setActiveTab('orders')}
            data-testid="orders-tab"
          >
            Orders ({orders.length})
          </Button>
        </div>

        {activeTab === 'products' ? (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            {/* Search + Filter Bar */}
            <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row gap-3">
              <Input
                placeholder="Search by Product ID, name, group, brand or barcode..."
                value={productSearch}
                onChange={(e) => { setProductSearch(e.target.value); setCurrentPage(1); }}
                className="flex-1"
                data-testid="admin-product-search-input"
              />
              <select
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
                className="h-10 px-3 border border-input rounded-md bg-background text-sm md:w-56"
                data-testid="admin-category-filter"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                className="h-10 px-3 border border-input rounded-md bg-background text-sm md:w-40"
                data-testid="admin-status-filter"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Product ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Group</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Brand</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Price</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Stock</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Top Selling</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Modified</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-6 py-10 text-center text-sm text-slate-500" data-testid="no-products-found">
                        No products match your filters.
                      </td>
                    </tr>
                  ) : paginatedProducts.map((product) => (
                    <tr key={product.id} className={`h-16 hover:bg-slate-50/70 transition-colors ${product.active === false ? 'opacity-60' : ''}`} data-testid={`product-row-${product.id}`}>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={product.active !== false}
                            onCheckedChange={() => handleToggleActive(product)}
                            className="data-[state=checked]:bg-green-600"
                            data-testid={`active-toggle-${product.id}`}
                          />
                          <span className={`text-xs font-semibold ${product.active !== false ? 'text-green-600' : 'text-red-500'}`} data-testid={`status-label-${product.id}`}>
                            {product.active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-sm font-mono font-semibold text-[#0047AB] whitespace-nowrap" data-testid={`product-code-${product.id}`}>
                        {product.product_code || '—'}
                      </td>
                      <td className="px-4 py-2 min-w-[260px]">
                        <div className="flex items-center gap-3">
                          <img
                            src={product.image_url || undefined}
                            alt={product.name}
                            className="w-11 h-11 object-contain rounded-md border border-slate-100 bg-white shrink-0"
                          />
                          <div className="min-w-0">
                            <p className="font-dm-sans font-semibold text-sm text-slate-900 truncate max-w-[340px]" title={product.name}>{product.name}</p>
                            <p className="text-xs text-slate-500 truncate max-w-[340px]">{product.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs whitespace-nowrap" data-testid={`product-group-${product.id}`}>
                        {product.group_name ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-700 font-medium max-w-[160px] truncate">{product.group_name}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-600 capitalize whitespace-nowrap">{product.category}</td>
                      <td className="px-4 py-2 text-sm text-slate-600 whitespace-nowrap">{product.brand || '—'}</td>
                      <td className="px-4 py-2 text-sm font-semibold text-slate-900 text-right whitespace-nowrap">
                        ₹{product.price.toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-flex min-w-[36px] justify-center px-2 py-0.5 rounded-full text-xs font-semibold ${product.stock === 0 ? 'bg-red-50 text-red-600' : product.stock <= 5 ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                          {product.stock}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!product.is_top_selling}
                          onChange={() => handleToggleTopSelling(product)}
                          className="w-4 h-4 accent-[#0047AB] cursor-pointer"
                          title="Show in Top Selling section on homepage"
                          data-testid={`top-selling-checkbox-${product.id}`}
                        />
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap" title={product.created_at ? new Date(product.created_at).toLocaleString() : ''} data-testid={`product-created-${product.id}`}>
                        {formatDate(product.created_at)}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap" title={product.updated_at ? new Date(product.updated_at).toLocaleString() : ''} data-testid={`product-updated-${product.id}`}>
                        {formatDate(product.updated_at)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/product/${product.id}`)}
                            title="View Product"
                            data-testid={`view-product-${product.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(product)}
                            title="Edit Product"
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setHistoryForProduct(product)}
                            title="View History"
                            data-testid={`history-product-${product.id}`}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            {filteredProducts.length > 0 && (
              <div className="px-4 py-3 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="products-pagination">
                <p className="text-sm text-slate-500" data-testid="pagination-summary">
                  Showing <span className="font-semibold text-slate-900">{(safePage - 1) * PAGE_SIZE + 1}</span>
                  –<span className="font-semibold text-slate-900">{Math.min(safePage * PAGE_SIZE, filteredProducts.length)}</span> of{' '}
                  <span className="font-semibold text-slate-900">{filteredProducts.length}</span> products
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={safePage === 1}
                      onClick={() => setCurrentPage(safePage - 1)}
                      data-testid="pagination-prev-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {getPageNumbers().map((p, idx) =>
                      p === '…' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-sm text-slate-400">…</span>
                      ) : (
                        <Button
                          key={p}
                          variant={p === safePage ? 'default' : 'outline'}
                          size="sm"
                          className={p === safePage ? 'bg-[#0047AB] hover:bg-[#003380] min-w-[36px]' : 'min-w-[36px]'}
                          onClick={() => setCurrentPage(p)}
                          data-testid={`pagination-page-${p}`}
                        >
                          {p}
                        </Button>
                      )
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={safePage === totalPages}
                      onClick={() => setCurrentPage(safePage + 1)}
                      data-testid="pagination-next-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Order ID</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Customer</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {orders.map((order) => (
                    <tr key={order.id} data-testid={`order-row-${order.id}`}>
                      <td className="px-6 py-4 text-sm font-mono text-slate-600">{order.id.slice(0, 8)}...</td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-dm-sans font-semibold text-slate-900">{order.customer_name}</p>
                          <p className="text-sm text-slate-500">{order.customer_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                        ₹{order.total_amount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          order.payment_status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {order.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isSuperAdmin && (
          <DangerZone onDataChanged={() => { fetchProducts(); fetchOrders(); }} />
        )}
      </div>

      {/* Product history modal */}
      <ProductHistoryModal
        open={!!historyForProduct}
        onOpenChange={(v) => { if (!v) setHistoryForProduct(null); }}
        product={historyForProduct}
      />
    </div>
  );
};

export default AdminPage;
